import { getBonusApi, getBonusEmployeeApi } from '@app/api/bonus.api';
import { BonusDataArray, BonusItem, BonusQueryParams, BonusModel } from '@app/interfaces/bonus';
import { AnyAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { showError } from '@app/utils/message';
import moment from 'moment';

export interface BonusState {
  data: BonusModel[];
  totalRows: number;
  pagination: {
    current: number,
    pageSize: number
  },
  filters: any,
  sorter: any,
  searchTerm: string;
  loading: boolean;
  error: string | null;
}

const initialState: BonusState = {
  data: [],
  totalRows: 0,
  pagination: { current: 1, pageSize: 20 },
  filters: {
    from_date: moment().startOf('month').format('YYYY-MM-DD'),
    to_date: moment().endOf('month').format('YYYY-MM-DD'),
  },
  sorter: {},
  searchTerm: "",
  loading: false,
  error: null,
};

export const getBonus = createAsyncThunk<BonusDataArray, BonusQueryParams>(
  'bonus/getBonus',
  async (params: BonusQueryParams, { rejectWithValue }) => {
    try {
      const customers = (await getBonusApi(params)) as BonusDataArray;
      return customers || [];
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  },
);

export const getBonuEmployee = createAsyncThunk<any, { employeeId: string, trans_month: string }>(
  'bonus/getBonusEmployee',
  async ({employeeId, trans_month}, { rejectWithValue }) => {
    try {
      const bonusData = await getBonusEmployeeApi(employeeId, { trans_month });
      return bonusData || [];
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'An error occurred'
      );
    }
  }
);

const isRejectedAction = (action: AnyAction) => {
  return action.type.endsWith('rejected');
};

// Slice
export const bonusSlice = createSlice({
  name: 'bonus',
  initialState,
  reducers: {
    updateTableParams(state, action) {
      const { pagination, filters, sorter, searchTerm } = action.payload;
      state.pagination = pagination;
      state.filters = filters;
      state.sorter = sorter;
      state.searchTerm = searchTerm;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getBonus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBonus.fulfilled, (state, action) => {
        state.loading = false;

        const data = action.payload.rows;
        state.data = data.map((item: BonusItem) => item.attributes) || [];

        state.totalRows = parseInt(String(action.payload.total_rows));
      })
      .addCase(getBonus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch products';
      })
      .addMatcher(isRejectedAction, (state, action) => {
        state.loading = false;
        showError(action.payload || action.error.message);
      });
  },
});

export const { updateTableParams } = bonusSlice.actions;

export default bonusSlice.reducer;
